import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Users, Search, CheckSquare, LayoutGrid, List, Archive } from 'lucide-react';
import { PageLoader } from '@shared/components/PageLoader';
import {
  Client,
  ClientLifecycleStage,
  LIFECYCLE_STAGES,
  NurtureCadence,
  isArchived,
  nextCheckInFor,
} from '@/types/crm';
import { toast } from 'sonner';
import { projectAPI } from '@/utils/supabase/client';
import { ClientPipelineBoard } from './ClientPipelineBoard';
import { ClientPipelineSummary } from './ClientPipelineSummary';
import { ClientSheet } from './ClientSheet';
import { ClientForm } from './ClientForm';
import { BulkActionBar } from './BulkActionBar';
import { ClientPipelineTable } from './ClientPipelineTable';
import { ClientArchive } from './ClientArchive';

export function CRMPage() {
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isClientSheetOpen, setIsClientSheetOpen] = useState(false);
  const [isClientCreateOpen, setIsClientCreateOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingClientId, setSavingClientId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'board' | 'list' | 'archive'>('board');
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [selectedClientIds, setSelectedClientIds] = useState<Set<string>>(new Set());
  // Churned clients are deliberately not part of the main load — there are 183
  // of them against ~40 live records, and pulling them on every CRM visit to
  // show them nowhere is waste. The archive fetches them the first time it is
  // opened and keeps them for the rest of the session.
  const [archivedClients, setArchivedClients] = useState<Client[]>([]);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [archiveLoaded, setArchiveLoaded] = useState(false);

  // Filter clients based on search query (only when 2+ characters)
  const matchesSearch = (client: Client) => {
    if (searchQuery.length < 2) return true;
    const query = searchQuery.toLowerCase();
    return (
      client.company_name.toLowerCase().includes(query) ||
      client.primary_contact_name.toLowerCase().includes(query) ||
      (client.primary_contact_email?.toLowerCase().includes(query) ?? false) ||
      (client.primary_contact_phone?.includes(query) ?? false)
    );
  };

  // The board and list show live pipeline only. Churned and paused live in the
  // archive: they were 83% of the records and drowned the work in flight.
  const filteredClients = clients.filter((c) => matchesSearch(c) && !isArchived(c));
  const filteredArchived = [...clients.filter(isArchived), ...archivedClients].filter(matchesSearch);

  // `silent` refreshes the data without swapping the board out for <PageLoader />.
  // Unmounting the board throws away the page's scroll offset and the board's own
  // horizontal scroll, so every save from the detail sheet would jump you back to
  // the top. Only the first load shows the spinner.
  const loadClients = async ({ silent = false }: { silent?: boolean } = {}) => {
    if (!silent) setLoading(true);
    try {
      const data = await projectAPI.getClients({ excludeChurned: true });
      setClients(data);
      // Keep the open sheet pointed at the freshly-loaded row, not the stale copy.
      setSelectedClient(prev => prev ? data.find(c => c.id === prev.id) ?? prev : prev);
    } catch (error) {
      console.error('Failed to load clients:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    loadClients();
  }, []);

  // Load the archive the first time it is opened. Paused records already came
  // down with the main load, so only churned needs fetching; combining the two
  // here keeps one definition of "archived" (isArchived) across both views.
  useEffect(() => {
    if (viewMode !== 'archive' || archiveLoaded || archiveLoading) return;
    setArchiveLoading(true);
    projectAPI
      .getClients({ stage: 'churned' })
      .then((churned) => {
        setArchivedClients(churned);
        setArchiveLoaded(true);
      })
      .catch((error) => {
        console.error('Failed to load archive:', error);
        toast.error('Failed to load the archive');
      })
      .finally(() => setArchiveLoading(false));
  }, [viewMode, archiveLoaded, archiveLoading]);

  // Set (or clear) how often an archived relationship should be revisited.
  // Setting a cadence stamps the next check-in date from today; clearing it
  // removes both, which takes the record out of the due list without deleting
  // any of its churn history.
  const setNurtureCadence = async (client: Client, cadence: NurtureCadence | null) => {
    const updates = cadence
      ? { nurture_cadence: cadence, next_check_in_on: nextCheckInFor(cadence) }
      : { nurture_cadence: null, next_check_in_on: null };

    const previous = {
      nurture_cadence: client.nurture_cadence,
      next_check_in_on: client.next_check_in_on,
    };

    const apply = (patch: Partial<Client>) =>
      setArchivedClients((prev) =>
        prev.map((c) => (c.id === client.id ? { ...c, ...patch } : c))
      );

    setSavingClientId(client.id);
    apply(updates as Partial<Client>);

    try {
      await projectAPI.updateClient(client.id, updates);
    } catch (error) {
      console.error('Failed to set nurture cadence:', error);
      apply(previous as Partial<Client>);
      toast.error('Failed to update check-in');
    } finally {
      setSavingClientId(null);
    }
  };

  // Optimistic stage transition shared by drag-and-drop, quick-move, and any other
  // board affordance. Lives here (not in the board) so the summary tiles, board
  // columns, and list view all update in the same frame.
  const transitionClient = async (client: Client, newStage: ClientLifecycleStage) => {
    if (client.lifecycle_stage === newStage) return;

    const previousStage = client.lifecycle_stage;
    const previousUpdatedAt = client.updated_at;

    // OPTIMISTIC UPDATE: reflect the move immediately everywhere
    setClients(prev =>
      prev.map(c => c.id === client.id
        ? { ...c, lifecycle_stage: newStage, updated_at: new Date().toISOString() }
        : c
      )
    );

    setSavingClientId(client.id);
    try {
      await projectAPI.transitionClient(client.id, { to_stage: newStage });
      toast.success(`Moved ${client.company_name} to ${LIFECYCLE_STAGES[newStage].label}`);
    } catch (error) {
      // ROLLBACK: revert only this client's stage. A whole-array snapshot would
      // clobber any other change (a second move, a sheet edit, a background
      // refresh) that landed while this request was in flight.
      setClients(prev =>
        prev.map(c => c.id === client.id
          ? { ...c, lifecycle_stage: previousStage, updated_at: previousUpdatedAt }
          : c
        )
      );
      console.error('Failed to transition client:', error);
      toast.error('Failed to move client. Card reverted to previous position.');
    } finally {
      setSavingClientId(null);
    }
  };

  const toggleBulkMode = () => {
    setIsBulkMode(prev => {
      if (prev) setSelectedClientIds(new Set());
      return !prev;
    });
  };

  const toggleClientSelection = (clientId: string) => {
    setSelectedClientIds(prev => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedClientIds.size === filteredClients.length) {
      setSelectedClientIds(new Set());
    } else {
      setSelectedClientIds(new Set(filteredClients.map(c => c.id)));
    }
  };

  const handleBulkTransition = async (toStage: ClientLifecycleStage) => {
    const ids = Array.from(selectedClientIds);
    const results = await Promise.allSettled(
      ids.map(id => projectAPI.transitionClient(id, { to_stage: toStage }))
    );
    const succeeded = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.filter(r => r.status === 'rejected').length;
    if (failed > 0) {
      toast.error(`${failed} of ${ids.length} transitions failed`);
    } else {
      toast.success(`Moved ${succeeded} clients to ${LIFECYCLE_STAGES[toStage].label}`);
    }
    setSelectedClientIds(new Set());
    setIsBulkMode(false);
    loadClients({ silent: true });
  };

  const handleClientClick = (client: Client) => {
    setSelectedClient(client);
    setIsClientSheetOpen(true);
  };

  const handleClientUpdated = () => {
    loadClients({ silent: true });
  };

  const handleClientSheetClose = () => {
    setIsClientSheetOpen(false);
    setSelectedClient(null);
    handleClientUpdated();
  };

  const handleClientCreateSuccess = () => {
    setIsClientCreateOpen(false);
    handleClientUpdated();
  };

  return (
    <div className="flex flex-1 flex-col gap-4 sm:gap-6 p-4 lg:p-6 overflow-y-auto overflow-x-hidden">
      {/* Header — stacks on phones so nothing hangs off-screen */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2 font-serif">
            <Users className="h-6 w-6" />
            Pipeline
          </h1>
          <p className="text-sm text-muted-foreground mt-1 hidden sm:block">
            Track clients through the lifecycle from lead to active
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search clients..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-full h-11 sm:h-9"
            />
          </div>
          {/* Board/list toggle is desktop-only; the table is unusable on phones */}
          <div className="hidden md:flex items-center border rounded-md">
            <Button
              variant={viewMode === 'board' ? 'secondary' : 'ghost'}
              size="icon"
              className="h-9 w-9 rounded-r-none"
              onClick={() => setViewMode('board')}
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'secondary' : 'ghost'}
              size="icon"
              className="h-9 w-9 rounded-none"
              onClick={() => setViewMode('list')}
            >
              <List className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'archive' ? 'secondary' : 'ghost'}
              size="icon"
              className="h-9 w-9 rounded-l-none"
              title="Archive — churned and paused"
              onClick={() => { setViewMode('archive'); setIsBulkMode(false); setSelectedClientIds(new Set()); }}
            >
              <Archive className="h-4 w-4" />
            </Button>
          </div>
          <Button
            variant={isBulkMode ? 'secondary' : 'outline'}
            className="h-11 sm:h-9 flex-1 sm:flex-initial"
            onClick={toggleBulkMode}
          >
            <CheckSquare className="h-4 w-4 mr-2" />
            {isBulkMode ? 'Cancel' : 'Bulk Edit'}
          </Button>
          <Button
            className="h-11 sm:h-9 flex-1 sm:flex-initial"
            onClick={() => setIsClientCreateOpen(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Client
          </Button>
        </div>
      </div>

      {/* Pipeline Summary — live pipeline only; the archive has its own counts */}
      {viewMode !== 'archive' && (
        <ClientPipelineSummary clients={filteredClients} loading={loading} />
      )}

      {/* Pipeline Board / List / Archive */}
      {loading ? (
        <PageLoader />
      ) : viewMode === 'archive' ? (
        archiveLoading && !archiveLoaded ? (
          <PageLoader />
        ) : (
          <ClientArchive
            clients={filteredArchived}
            onClientClick={handleClientClick}
            onSetCadence={setNurtureCadence}
            savingClientId={savingClientId}
          />
        )
      ) : viewMode === 'board' ? (
        <ClientPipelineBoard
          clients={filteredClients}
          onClientClick={isBulkMode ? undefined : handleClientClick}
          onMoveClient={transitionClient}
          savingClientId={savingClientId}
          isBulkMode={isBulkMode}
          selectedClientIds={selectedClientIds}
          onToggleSelect={isBulkMode ? toggleClientSelection : undefined}
        />
      ) : (
        <ClientPipelineTable
          clients={filteredClients}
          onClientClick={handleClientClick}
          selectedClientIds={selectedClientIds}
          onToggleSelect={toggleClientSelection}
          onToggleSelectAll={toggleSelectAll}
        />
      )}

      {/* Client Detail Sheet */}
      <ClientSheet
        client={selectedClient}
        open={isClientSheetOpen}
        onOpenChange={(open) => {
          if (!open) handleClientSheetClose();
        }}
        onUpdate={handleClientUpdated}
      />

      {/* Bulk Action Bar */}
      {selectedClientIds.size > 0 && (
        <BulkActionBar
          selectedCount={selectedClientIds.size}
          onTransition={handleBulkTransition}
          onCancel={() => { setSelectedClientIds(new Set()); if (isBulkMode) setIsBulkMode(false); }}
        />
      )}

      {/* Create Client Form */}
      <ClientForm
        open={isClientCreateOpen}
        onOpenChange={setIsClientCreateOpen}
        onSuccess={handleClientCreateSuccess}
      />
    </div>
  );
}
